import { CloseOutlined, DownloadOutlined, FilterAltOutlined, Search, StarBorder, ViewWeekOutlined } from '@mui/icons-material'
import { Box, Button, IconButton, InputAdornment, Stack, Theme, Tooltip, Typography } from '@mui/material'
import {
  GridColDef,
  GridFilterModel,
  gridFilterModelSelector,
  GridSlotsComponentsProps,
  GridToolbarColumnsButton,
  GridToolbarExport,
  GridToolbarFilterButton,
  GridToolbarQuickFilter,
  useGridApiContext,
  useGridSelector,
} from '@mui/x-data-grid-premium'
import uniq from 'lodash/uniq'
import { PropsWithChildren, ReactNode, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useFeatureFlags } from '@omnea-app/fe-org-settings/FeatureFlagsProvider'
import { requestsTableName } from '@omnea-app/fe-requests/requestsTableName'
import { useIsThemeV2 } from '@omnea-app/omui/NewThemeProvider'
import { filterItemIsValid } from '@omnea-app/packages/universal/saved-filters/validateFilterModel'
import { isDefined } from '@omnea-app/packages/universal/type-guards/isDefined'
import { AiFilterCreator } from '../../ai-filter-creator/AiFilterCreator'
import { RowGrouping } from '../../row-grouping/RowGrouping'
import { useSavedFilterContext } from './SavedFilterContext'

export type ExportOptions = {
  fileName: string
  fields?: string[]
}

declare module '@mui/x-data-grid-premium' {
  interface ToolbarPropsOverrides {
    tableName: string
    exportOptions: ExportOptions
    displayRowGroupingButton?: boolean
    hideSearchBar?: boolean
    disableExportInToolbar?: boolean
    exportLoading?: boolean
    isToolbarEmpty?: boolean
    header?: ReactNode
    onExportClick?: () => void
  }
}

export const ControlledTableToolbar = ({
  tableName,
  exportOptions,
  displayRowGroupingButton,
  hideSearchBar,
  disableExportInToolbar,
  exportLoading,
  header,
  isToolbarEmpty,
  onExportClick,
}: NonNullable<GridSlotsComponentsProps['toolbar']>) => {
  const { t } = useTranslation('common')
  const { savedFilter, onUnselectSavedFilter } = useSavedFilterContext()
  const apiRef = useGridApiContext()
  const { GEN_AI } = useFeatureFlags()

  const areColumnsGroupable = useMemo(() => {
    const columns = apiRef.current?.getAllColumns()
    return columns?.some((column) => column.groupable)
  }, [apiRef])

  const filterModel = useGridSelector(apiRef, gridFilterModelSelector)

  const filteredColumnsNames = getFilteredColumnNames(filterModel, apiRef.current?.getColumn)

  const isThemeV2 = useIsThemeV2()

  if (isToolbarEmpty) return null

  return (
    <Stack
      sx={
        isThemeV2
          ? (theme) => ({
              backgroundColor: 'futureTheme.bg.surface.default',
              borderBottomStyle: 'solid',
              borderBottomWidth: theme.futureTheme.strokeWidth.default,
              borderBottomColor: theme.palette.futureTheme.border.strong,
            })
          : {
              bgcolor: 'neutralLight.0',
              minWidth: '100%',
              borderTopLeftRadius: '8px',
              borderTopRightRadius: '8px',
              border: '1px solid #E7E7ED',
              borderBottom: 'none',
            }
      }
    >
      {header && header}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={isThemeV2 ? (theme) => ({ padding: theme.spacing(2.5, 2.5, 2.5, 3) }) : { py: 2, px: 4 }}
      >
        <Stack
          sx={(theme: Theme) =>
            isThemeV2
              ? {
                  flexDirection: 'row',
                  gap: 1.5,
                }
              : {
                  flexDirection: 'row',
                  gap: 3,
                  '& .MuiButton-startIcon': {
                    display: 'flex',
                    justifyContent: 'center',
                    borderRadius: '4px',

                    margin: 0,
                    padding: 0,

                    textTransform: 'none',
                  },
                  '& > *': {
                    height: 24,
                    borderRadius: '8px',

                    margin: 0,
                  },
                  '& .MuiButtonBase-root': {
                    padding: '3px 6px',
                    background: 'none',
                    '&:hover': {
                      backgroundColor: theme.palette.neutralLight[50],
                    },
                    '&.active': {
                      border: `1px solid ${theme.palette.neutralLight[300]}`,
                      backgroundColor: theme.palette.neutralLight[50],
                      '&:hover': {
                        backgroundColor: theme.palette.neutralLight[100],
                      },
                    },
                  },
                }
          }
        >
          {savedFilter && <SavedFilterBadge>{savedFilter.name}</SavedFilterBadge>}

          <GridToolbarFilterButton
            slotProps={{
              button: {
                className: filteredColumnsNames ? 'active' : undefined,
                color: isThemeV2 ? 'secondary' : 'neutralLight',
                startIcon: (
                  <Stack
                    direction="row"
                    gap={0.5}
                    sx={
                      isThemeV2
                        ? {}
                        : {
                            color: '#6B6C7E',
                            justifyContent: 'center',
                            alignItems: 'center',
                          }
                    }
                  >
                    <FilterAltOutlined sx={{ width: 16, m: 0, p: 0 }} />
                    <Typography sx={{ m: 0, p: 0, ml: '1px' }} variant="Body14pxMedium">
                      {filteredColumnsNames
                        ? t('table.table-toolbar.active-filter-button-label', { columnList: filteredColumnsNames.join(', ') })
                        : t('table.table-toolbar.empty-filter-button-label')}
                    </Typography>
                    {filteredColumnsNames && filteredColumnsNames.length > 0 && (
                      <IconButton
                        size={isThemeV2 ? 'small' : undefined}
                        sx={
                          isThemeV2
                            ? {
                                padding: 0,
                                // Right margin offsets the startIcon right margin
                                marginRight: -2,
                              }
                            : { padding: '0px !important', margin: '1px -2px 0px 2px' }
                        }
                        onClick={(e) => {
                          e.stopPropagation()
                          apiRef.current?.setFilterModel({ items: [] })
                          if (savedFilter) {
                            onUnselectSavedFilter()
                          }
                        }}
                      >
                        <CloseOutlined sx={{ fontSize: '16px' }} />
                      </IconButton>
                    )}
                  </Stack>
                ),
                variant: isThemeV2 ? 'text' : 'contained',
              },
            }}
          />
          {/* eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- FIXME: bulk migration to add error  */}
          {GEN_AI && tableName === requestsTableName && <AiFilterCreator tableName={tableName!} apiRef={apiRef} />}
          {displayRowGroupingButton && areColumnsGroupable && <RowGrouping />}
          <Tooltip title={t('table.table-toolbar.tooltip-columns')}>
            <GridToolbarColumnsButton
              slotProps={{
                button: {
                  color: isThemeV2 ? 'secondary' : 'neutralLight',
                  variant: isThemeV2 ? 'text' : undefined,
                  startIcon: (
                    <Stack
                      direction="row"
                      gap={0.5}
                      sx={
                        isThemeV2
                          ? {}
                          : { color: '#6B6C7E', justifyContent: 'center', alignItems: 'center', textTransform: 'none' }
                      }
                    >
                      <ViewWeekOutlined sx={{ width: 16 }} />
                      <Typography sx={{ m: 0, p: 0, ml: '1px' }} variant="Body14pxMedium">
                        {t('table.table-toolbar.columns-button-label')}
                      </Typography>
                    </Stack>
                  ),
                },
              }}
            />
          </Tooltip>
          {!disableExportInToolbar && (
            <Tooltip title={exportLoading ? t('table.table-toolbar.tooltip-export-loading') : ''}>
              <span style={{ display: 'flex', alignItems: 'center' }}>
                {onExportClick != null ? (
                  <Button
                    color={isThemeV2 ? 'secondary' : 'neutralLight'}
                    variant={isThemeV2 ? 'text' : 'contained'}
                    disabled={exportLoading}
                    onClick={onExportClick}
                    sx={exportLoading && !isThemeV2 ? { '&.Mui-disabled': { backgroundColor: 'transparent' } } : {}}
                    startIcon={
                      <Stack
                        direction="row"
                        gap={0.5}
                        sx={(theme: Theme) => ({
                          color: exportLoading && !isThemeV2 ? theme.palette.neutralLight[300] : 'inherit',
                          justifyContent: 'center',
                          alignItems: 'center',
                        })}
                      >
                        <DownloadOutlined sx={{ width: 16 }} />
                        <Typography sx={{ m: 0, p: 0, ml: '1px' }} variant="Body14pxMedium">
                          {t('table.table-toolbar.export-button-label')}
                        </Typography>
                      </Stack>
                    }
                  />
                ) : (
                  <GridToolbarExport
                    csvOptions={exportOptions}
                    excelOptions={exportOptions}
                    slotProps={{
                      button: {
                        color: isThemeV2 ? 'secondary' : 'neutralLight',
                        variant: isThemeV2 ? 'text' : 'contained',
                        disabled: exportLoading,
                        sx: exportLoading && !isThemeV2 ? { '&.Mui-disabled': { backgroundColor: 'transparent' } } : {},
                        startIcon: (
                          <Stack
                            direction="row"
                            gap={0.5}
                            sx={(theme: Theme) => ({
                              color: exportLoading && !isThemeV2 ? theme.palette.neutralLight[300] : 'inherit',
                              justifyContent: 'center',
                              alignItems: 'center',
                            })}
                          >
                            <DownloadOutlined sx={{ width: 16 }} />
                            <Typography sx={{ m: 0, p: 0, ml: '1px' }} variant="Body14pxMedium">
                              {t('table.table-toolbar.export-button-label')}
                            </Typography>
                          </Stack>
                        ),
                      },
                    }}
                  />
                )}
              </span>
            </Tooltip>
          )}
        </Stack>
        {!hideSearchBar && (
          <GridToolbarQuickFilter
            variant={isThemeV2 ? undefined : 'outlined'}
            placeholder={t('search')}
            debounceMs={500}
            sx={isThemeV2 ? { marginBottom: 0 } : undefined}
            InputProps={
              isThemeV2
                ? {
                    startAdornment: SearchStartAdornment,
                    endAdornment: null,
                    size: 'small',
                  }
                : {
                    startAdornment: null,
                    endAdornment: SearchEndAdornment,
                    sx: {
                      borderRadius: 8,
                      height: '28px',
                      width: 250,
                      mt: '3px',
                      bgcolor: 'neutralLight.0',
                    },
                  }
            }
          />
        )}
      </Stack>
    </Stack>
  )
}
const SearchStartAdornment = (
  <InputAdornment position="start">
    <Search sx={{ width: 18 }} />
  </InputAdornment>
)
const SearchEndAdornment = (
  <InputAdornment position="end">
    <IconButton edge="end" sx={{ position: 'absolute', right: 10 }}>
      <Search sx={{ width: 18, transform: 'scaleX(-1)' }} />
    </IconButton>
  </InputAdornment>
)

const getFilteredColumnNames = (
  filterModel: GridFilterModel | undefined,
  getColumn: undefined | ((field: string) => GridColDef)
): string[] | null => {
  if (!filterModel || !getColumn) return null
  const filterItems = filterModel?.items

  if (!filterItems) return null

  const allFilteredColumnNames = filterItems.filter(filterItemIsValid).map(({ field }) => field)

  if (allFilteredColumnNames.length < 1) return null

  const uniqueNames = uniq(allFilteredColumnNames)
    .map(getColumn)
    .map((columnDef) => columnDef?.headerName)
    .filter(isDefined)

  if (uniqueNames.length < 1) return null

  return uniqueNames
}

const SavedFilterBadge: React.FC<PropsWithChildren> = ({ children }) => {
  return (
    <Box
      sx={(theme: Theme) => ({
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '2px',

        padding: '3px 6px',
        color: theme.palette.neutralLight[50],
        backgroundColor: theme.palette.neutralDark[700],
      })}
    >
      <StarBorder sx={{ width: 16, height: 16 }} />
      <Typography variant="Body12pxMedium">{children}</Typography>
    </Box>
  )
}
